import React, { useState, useEffect } from 'react';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import TemplateManager from './TemplateManager';
import CampaignManager from './CampaignManager';
import SendMessage from './SendMessage';
import './AdminDashboard.css';

const AdminDashboard = ({ signOut, user }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userAttributes, setUserAttributes] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userSettings, setUserSettings] = useState({
    leadsPerPage: 50,
    sortOrder: 'newest',
    autoRefresh: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editingField, setEditingField] = useState(null); // { qrCode, field }
  const [sendMessageLead, setSendMessageLead] = useState(null);

  const loadUserSettings = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      const response = await fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const settings = await response.json();
        console.log('Loaded user settings:', settings);
        setUserSettings(settings);
        return settings;
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveUserSettings = async (newSettings) => {
    setSettingsLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      const response = await fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        setUserSettings(newSettings);
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      const settings = await loadUserSettings();
      fetchUserInfo();
      if (settings) {
        fetchLeadsWithSettings(1, settings);
      } else {
        fetchLeads(1);
      }
    };
    initializeApp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserInfo = async () => {
    try {
      const attributes = await fetchUserAttributes();
      setUserAttributes(attributes);
    } catch (error) {
      console.error('Error fetching user attributes:', error);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      const csvText = await uploadFile.text();

      const response = await fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/upload-csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'text/csv'
        },
        body: csvText
      });

      if (response.ok) {
        alert('CSV uploaded successfully!');
        setUploadFile(null);
        fetchLeads(1);
      } else {
        const errorText = await response.text();
        alert(`Upload failed: ${errorText}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const fetchLeadsWithSettings = async (page = 1, settings = null) => {
    const effectiveSettings = settings || userSettings;
    console.log('fetchLeads called with leadsPerPage:', effectiveSettings.leadsPerPage);
    
    try {
      setLoading(true);

      const session = await fetchAuthSession();
      const authToken = session.tokens.idToken.toString();
      
      let url = 'https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads';
      const params = new URLSearchParams();
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('page', page.toString());
      params.append('limit', effectiveSettings.leadsPerPage.toString());
      
      console.log('API URL with params:', url + '?' + params.toString());
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('API response:', data);
        console.log('Number of leads returned:', data.leads?.length);
        
        setLeads(data.leads || []);
        setCurrentPage(page);
        setTotalLeads(data.totalCount || 0);
        setTotalPages(Math.ceil((data.totalCount || 0) / effectiveSettings.leadsPerPage));
      } else {
        throw new Error('Failed to fetch leads');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = (page = 1) => {
    return fetchLeadsWithSettings(page, null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLeads(1);
  };

  const [statusModal, setStatusModal] = useState({ show: false, qrCode: '', newStatus: '', currentStatus: '' });
  const [resubscribeModal, setResubscribeModal] = useState({ show: false, qrCode: '', leadName: '' });

  const updateLeadStatus = async (qrCode, newStatus, note = '') => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(`https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads/${qrCode}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, note })
      });

      if (response.ok) {
        setLeads(leads.map(lead => 
          lead.qrCode === qrCode ? { ...lead, status: newStatus } : lead
        ));
      } else {
        alert('Failed to update lead status');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Failed to update lead status');
    }
  };

  const handleStatusChange = (qrCode, newStatus, currentStatus) => {
    if (newStatus === 'resubscribe') {
      const lead = leads.find(l => l.qrCode === qrCode);
      const leadName = lead?.name || lead?.email || 'Unknown Lead';
      setResubscribeModal({ show: true, qrCode, leadName });
    } else {
      setStatusModal({ show: true, qrCode, newStatus, currentStatus });
    }
  };

  const confirmStatusChange = () => {
    const note = document.getElementById('statusNote').value;
    updateLeadStatus(statusModal.qrCode, statusModal.newStatus, note);
    setStatusModal({ show: false, qrCode: '', newStatus: '', currentStatus: '' });
  };

  const resubscribeLead = async (qrCode) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(`https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads/${qrCode}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'resubscribe',
          resetStatus: true // Reset status from USER_UNSUBSCRIBED to new
        })
      });

      if (response.ok) {
        // Update the lead in the local state
        setLeads(leads.map(lead => 
          lead.qrCode === qrCode ? { 
            ...lead, 
            contactStatus: 'subscribed', 
            status: 'new',
            resubscribedAt: new Date().toISOString()
          } : lead
        ));
        alert('Lead successfully re-subscribed!');
      } else {
        alert('Failed to re-subscribe lead');
      }
    } catch (error) {
      console.error('Error re-subscribing lead:', error);
      alert('Failed to re-subscribe lead');
    }
  };

  const confirmResubscribe = () => {
    resubscribeLead(resubscribeModal.qrCode);
    setResubscribeModal({ show: false, qrCode: '', leadName: '' });
  };

  const startEditingField = (qrCode, field, currentValue) => {
    setEditingField({ qrCode, field });
    setEditForm({ [field]: currentValue || '' });
  };

  const saveField = async (qrCode, field) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(`https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads/${qrCode}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: editForm[field] })
      });

      if (response.ok) {
        setLeads(leads.map(lead => 
          lead.qrCode === qrCode ? { ...lead, [field]: editForm[field] } : lead
        ));
        setEditingField(null);
        setEditForm({});
      } else {
        alert('Failed to update field');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Failed to update field');
    }
  };

  const cancelFieldEdit = () => {
    setEditingField(null);
    setEditForm({});
  };

  const deleteLead = async (qrCode) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(`https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads/${qrCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setLeads(leads.filter(lead => lead.qrCode !== qrCode));
        alert('Lead deleted successfully');
      } else {
        alert('Failed to delete lead');
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead');
    }
  };

  const handleExportMailList = async () => {
    try {
      // Fetch ALL leads for export
      const session = await fetchAuthSession();
      const authToken = session.tokens.idToken.toString();
      
      const response = await fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/leads?limit=1000', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        alert('Failed to fetch leads for export');
        return;
      }

      const data = await response.json();
      const allLeads = data.leads || [];

      if (allLeads.length === 0) {
        alert('No leads to export');
        return;
      }

      const hasBlankNames = allLeads.some(lead => !lead.firstName || !lead.lastName);
      let defaultFirstName = '';
      let defaultLastName = '';

      if (hasBlankNames) {
        const defaultName = prompt('If the lead does not have a person\'s name, what recipient name should be used?');
        if (!defaultName) return;
        const nameParts = defaultName.trim().split(' ');
        defaultFirstName = nameParts[0] || '';
        defaultLastName = nameParts.slice(1).join(' ') || '';
      }

      const domain = prompt('What domain should be used for QR URLs? (e.g., stoprentregret.com)');
      if (!domain) return;

      const csvContent = allLeads.map(lead => {
        const firstName = lead.firstName || defaultFirstName;
        const lastName = lead.lastName || defaultLastName;
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Use the actual address attribute, not concatenated city/state/zip
        const address = lead.address || '';
        const city = lead.city || '';
        const state = lead.state || '';
        const zip = lead.zip || '';
        
        return [
          `"${fullName}"`,
          `"${address}"`,
          `"${city}"`,
          `"${state}"`,
          `"${zip}"`,
          `"https://${domain}/?qr=${lead.qrCode || ''}"`
        ].join(',');
      }).join('\n');

      const header = 'Name,Address,City,State,Zip,QR_URL\n';
      const blob = new Blob([header + csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export leads');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#e74c3c';
      case 'contacted': return '#f39c12';
      case 'scheduled': return '#3498db';
      case 'showed': return '#9b59b6';
      case 'closed': return '#27ae60';
      case 'resubscribe': return '#e67e22';
      default: return '#95a5a6';
    }
  };

  const fetchDashboardMetrics = async () => {
    setDashboardLoading(true);
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens.idToken.toString();
      
      const response = await fetch('https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Dashboard data received:', data);
        setDashboardData(data);
      } else {
        console.error('Failed to fetch dashboard metrics');
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'dashboard') {
      fetchDashboardMetrics();
    }
  }, [currentView]);

  const renderDashboardWidgets = () => {
    if (dashboardLoading) {
      return (
        <div className="dashboard-widgets">
          <div className="widget">
            <h3>Loading dashboard metrics...</h3>
          </div>
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div className="dashboard-widgets">
          <div className="widget">
            <h3>No dashboard data available</h3>
          </div>
        </div>
      );
    }

    const { hotLeads, actionRequired, emailMetrics, qualityLeads } = dashboardData;

    return (
      <div className="dashboard-widgets">
        {/* Hot Leads Alert */}
        {hotLeads.length > 0 && (
          <div className="widget hot-leads-alert">
            <h3>🔥 Hot Leads (Recent Email Opens)</h3>
            <div className="hot-leads-list">
              {hotLeads.slice(0, 3).map(lead => (
                <div key={lead.qrCode} className="hot-lead-item">
                  <div className="lead-info">
                    <strong>{lead.firstName} {lead.lastName}</strong>
                    <span className="email">{lead.email}</span>
                    <span className="phone">{lead.phone}</span>
                  </div>
                  <div className="engagement-info">
                    <span className="time-ago">
                      Opened {Math.round((new Date() - new Date(lead.lastEmailEventTime)) / (1000 * 60))} min ago
                    </span>
                    <span className="status">{lead.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Required Dashboard */}
        <div className="widget action-required">
          <h3>⚡ Action Required</h3>
          <div className="action-items">
            {actionRequired.newLeads.length > 0 && (
              <div className="action-item">
                <span className="count">{actionRequired.newLeads.length}</span>
                <span className="description">New leads need first contact</span>
              </div>
            )}
            {actionRequired.emailOpeners.length > 0 && (
              <div className="action-item priority">
                <span className="count">{actionRequired.emailOpeners.length}</span>
                <span className="description">Email openers need follow-up</span>
              </div>
            )}
            {actionRequired.staleLeads.length > 0 && (
              <div className="action-item warning">
                <span className="count">{actionRequired.staleLeads.length}</span>
                <span className="description">Stale leads (>48h old)</span>
              </div>
            )}
          </div>
        </div>

        {/* Email Performance Metrics */}
        <div className="widget email-metrics">
          <h3>📧 Email Performance</h3>
          <div className="metrics-grid">
            <div className="metric">
              <span className="metric-value">{emailMetrics.openRate}%</span>
              <span className="metric-label">Open Rate</span>
            </div>
            <div className="metric">
              <span className="metric-value">{emailMetrics.deliveryRate}%</span>
              <span className="metric-label">Delivery Rate</span>
            </div>
            <div className="metric">
              <span className="metric-value">{emailMetrics.recentOpens}</span>
              <span className="metric-label">Opens (24h)</span>
            </div>
            <div className="metric">
              <span className="metric-value">{emailMetrics.totalEmails}</span>
              <span className="metric-label">Total Emails</span>
            </div>
          </div>
        </div>

        {/* Lead Quality Scoring */}
        <div className="widget lead-quality">
          <h3>⭐ Top Quality Leads</h3>
          <div className="quality-leads">
            {qualityLeads.map(lead => (
              <div key={lead.qrCode} className="quality-lead-item">
                <div className="lead-name">
                  <strong>{lead.firstName} {lead.lastName}</strong>
                  <span className="score">Score: {lead.score}/10</span>
                </div>
                <div className="lead-details">
                  <span>Visits: {lead.visitCount}</span>
                  <span>Status: {lead.status}</span>
                  <span>Email: {lead.mailStatus || 'unknown'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (currentView === 'dashboard') {
      return (
        <div className="dashboard-view">
          <div className="stats">
            <div className="stat-card">
              <h3>Total Leads</h3>
              <p>{totalLeads}</p>
            </div>
            <div className="stat-card">
              <h3>New Leads</h3>
              <p>{leads.filter(lead => lead.status === 'new').length}</p>
            </div>
            <div className="stat-card">
              <h3>Scheduled</h3>
              <p>{leads.filter(lead => lead.status === 'scheduled').length}</p>
            </div>
          </div>
          {renderDashboardWidgets()}
        </div>
      );
    }
    
    if (currentView === 'import') {
      return (
        <div className="import-view">
          <h2>Import Leads</h2>
          <div className="upload-section">
            <form onSubmit={handleFileUpload} className="csv-upload">
              <label htmlFor="csv-file">Select CSV File</label>
              <input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
                disabled={uploading}
              />
              <button type="submit" disabled={!uploadFile || uploading} className="upload-btn">
                {uploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </form>
            <div className="help-section">
              <button onClick={() => setShowHelp(true)} className="help-btn">
                CSV Format Help
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    if (currentView === 'export') {
      return (
        <div className="export-view">
          <h2>Export Leads</h2>
          <button onClick={handleExportMailList} className="export-btn">
            Export Mail List
          </button>
        </div>
      );
    }
    
    if (currentView === 'settings') {
      return (
        <div className="settings-view">
          <h2>My Settings</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const newSettings = {
              leadsPerPage: parseInt(formData.get('leadsPerPage')),
              sortOrder: formData.get('sortOrder'),
              autoRefresh: formData.get('autoRefresh') === 'on'
            };
            saveUserSettings(newSettings);
          }}>
            <div className="settings-section">
              <h3>Display Settings</h3>
              
              <div className="setting-item">
                <label htmlFor="leadsPerPage">Leads per page:</label>
                <select 
                  id="leadsPerPage" 
                  name="leadsPerPage" 
                  defaultValue={userSettings.leadsPerPage}
                  disabled={settingsLoading}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>

              <div className="setting-item">
                <label htmlFor="sortOrder">Sort order:</label>
                <select 
                  id="sortOrder" 
                  name="sortOrder" 
                  defaultValue={userSettings.sortOrder}
                  disabled={settingsLoading}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">By name</option>
                </select>
              </div>

              <div className="setting-item">
                <label htmlFor="autoRefresh">
                  <input 
                    type="checkbox" 
                    id="autoRefresh" 
                    name="autoRefresh" 
                    defaultChecked={userSettings.autoRefresh}
                    disabled={settingsLoading}
                  />
                  Auto-refresh leads every 30 seconds
                </label>
              </div>
            </div>

            <div className="settings-actions">
              <button type="submit" disabled={settingsLoading} className="save-settings-btn">
                {settingsLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      );
    }
    
    if (currentView === 'campaigns') {
      return <CampaignManager />;
    }

    if (currentView === 'templates') {
      return <TemplateManager />;
    }
    
    // Default leads view
    return (
      <>
        <div className="stats">
          <div className="stat-card">
            <h3>Total Leads</h3>
            <p>{totalLeads}</p>
          </div>
          <div className="stat-card">
            <h3>New Leads</h3>
            <p>{leads.filter(lead => lead.status === 'new').length}</p>
          </div>
          <div className="stat-card">
            <h3>Scheduled</h3>
            <p>{leads.filter(lead => lead.status === 'scheduled').length}</p>
          </div>
        </div>
        
        <div className="dashboard-actions">
          <div className="search-section">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">Search</button>
              {searchTerm && (
                <button 
                  type="button" 
                  onClick={() => { setSearchTerm(''); setCurrentPage(1); fetchLeads(1); }}
                  className="clear-search-btn"
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>

        <div className="leads-table" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: '1200px', tableLayout: 'auto', width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>First Name</th>
                <th style={{ whiteSpace: 'nowrap' }}>Last Name</th>
                <th style={{ whiteSpace: 'nowrap' }}>Email</th>
                <th style={{ whiteSpace: 'nowrap' }}>Phone</th>
                <th style={{ whiteSpace: 'nowrap' }}>Address</th>
                <th style={{ whiteSpace: 'nowrap' }}>City</th>
                <th style={{ whiteSpace: 'nowrap' }}>State</th>
                <th style={{ whiteSpace: 'nowrap' }}>Zip</th>
                <th style={{ whiteSpace: 'nowrap' }}>Visits</th>
                <th style={{ whiteSpace: 'nowrap' }}>Last Visit</th>
                <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                <th style={{ whiteSpace: 'nowrap' }}>Mail Status</th>
                <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.qrCode}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'firstName' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                          style={{ width: '80px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'firstName')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.firstName || 'N/A'}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'firstName', lead.firstName)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'lastName' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                          style={{ width: '80px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'lastName')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.lastName || 'N/A'}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'lastName', lead.lastName)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'email' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          style={{ width: '120px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'email')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.email || 'N/A'}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'email', lead.email)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'phone' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.phone}
                          onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                          style={{ width: '100px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'phone')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.phone || 'N/A'}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'phone', lead.phone)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'address' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.address}
                          onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                          style={{ width: '120px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'address')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.address}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'address', lead.address)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'city' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                          style={{ width: '80px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'city')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.city}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'city', lead.city)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'state' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.state}
                          onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                          style={{ width: '40px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'state')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.state}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'state', lead.state)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingField?.qrCode === lead.qrCode && editingField?.field === 'zip' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="text"
                          value={editForm.zip}
                          onChange={(e) => setEditForm({...editForm, zip: e.target.value})}
                          style={{ width: '60px' }}
                          autoFocus
                        />
                        <button onClick={() => saveField(lead.qrCode, 'zip')} style={{ padding: '2px 6px', fontSize: '12px' }}>✓</button>
                        <button onClick={cancelFieldEdit} style={{ padding: '2px 6px', fontSize: '12px' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{lead.zip}</span>
                        <button onClick={() => startEditingField(lead.qrCode, 'zip', lead.zip)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{lead.visitCount || 0}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{lead.lastVisit ? new Date(lead.lastVisit).toLocaleDateString() : 'Never'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <select 
                      value={lead.status || 'new'} 
                      onChange={(e) => handleStatusChange(lead.qrCode, e.target.value, lead.status || 'new')}
                      style={{ 
                        backgroundColor: getStatusColor(lead.status || 'new'),
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="showed">Showed</option>
                      <option value="closed">Closed</option>
                      {(lead.contactStatus === 'unsubscribed' || lead.status === 'USER_UNSUBSCRIBED') && (
                        <option value="resubscribe">Re-subscribe</option>
                      )}
                      <option value="USER_UNSUBSCRIBED">Unsubscribed</option>
                    </select>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      backgroundColor: lead.contactStatus === 'unsubscribed' ? '#e74c3c' :
                                     lead.mailStatus === 'delivered' ? '#27ae60' : 
                                     lead.mailStatus === 'opened' ? '#2ecc71' :
                                     lead.mailStatus === 'hard_bounced' ? '#e74c3c' :
                                     lead.mailStatus === 'complained' ? '#8e44ad' : '#95a5a6',
                      color: 'white'
                    }}>
                      {lead.contactStatus === 'unsubscribed' ? 'unsubscribed' : lead.mailStatus || 'unknown'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button 
                      onClick={() => setSendMessageLead(lead)}
                      className="send-message-btn"
                      title="Send message"
                      disabled={!lead.email && !lead.phone}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', marginRight: '8px' }}
                    >
                      📧
                    </button>
                    <button 
                      onClick={() => deleteLead(lead.qrCode)}
                      className="delete-btn"
                      title="Delete lead"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {totalPages > 1 && (
            <div className="pagination-section">
              <div className="pagination-info">
                Showing {((currentPage - 1) * userSettings.leadsPerPage) + 1} to {Math.min(currentPage * userSettings.leadsPerPage, totalLeads)} of {totalLeads} leads
              </div>
              <div className="pagination-controls">
                <button 
                  onClick={() => fetchLeads(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  First
                </button>
                <button 
                  onClick={() => fetchLeads(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchLeads(pageNum)}
                      className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button 
                  onClick={() => fetchLeads(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
                <button 
                  onClick={() => fetchLeads(totalPages)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return <div className="loading">Loading leads...</div>;
  }

  return (
    <div className="admin-dashboard">
      {/* Hamburger Menu */}
      <div className={`menu-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)}></div>
      <nav className={`sidebar-menu ${menuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          <h3>Menu</h3>
          <button className="close-menu" onClick={() => setMenuOpen(false)}>×</button>
        </div>
        <ul className="menu-list">
          <li className="menu-section">
            <span className="menu-title">Dashboard</span>
            <ul className="submenu">
              <li><button type="button" onClick={() => { setCurrentView('dashboard'); setMenuOpen(false); }}>Analytics</button></li>
            </ul>
          </li>
          <li className="menu-section">
            <span className="menu-title">Leads</span>
            <ul className="submenu">
              <li><button type="button" onClick={() => { setCurrentView('leads'); setMenuOpen(false); }}>Display Leads</button></li>
              <li><button type="button" onClick={() => { setCurrentView('import'); setMenuOpen(false); }}>Import List</button></li>
              <li><button type="button" onClick={() => { setCurrentView('export'); setMenuOpen(false); }}>Export List</button></li>
            </ul>
          </li>
          <li className="menu-section">
            <span className="menu-title">WorkFlows</span>
            <ul className="submenu">
              <li><button type="button" onClick={() => { setCurrentView('campaigns'); setMenuOpen(false); }}>Campaigns</button></li>
              <li><button type="button" onClick={() => { setCurrentView('templates'); setMenuOpen(false); }}>Templates</button></li>
            </ul>
          </li>
          <li className="menu-section">
            <span className="menu-title">Settings</span>
            <ul className="submenu">
              <li><button type="button" onClick={() => { setCurrentView('settings'); setMenuOpen(false); }}>MySettings</button></li>
            </ul>
          </li>
          <li className="menu-section">
            <span className="menu-title">Help</span>
            <ul className="submenu">
              <li><button type="button" onClick={() => { setShowHelp(true); setMenuOpen(false); }}>Search Help</button></li>
            </ul>
          </li>
        </ul>
      </nav>

      <header className="dashboard-header">
        <div className="header-top">
          <button className="hamburger-menu" onClick={() => setMenuOpen(true)}>
            ☰
          </button>
          <h1>Lead Management Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {userAttributes?.given_name && userAttributes?.family_name 
              ? `${userAttributes.given_name} ${userAttributes.family_name}` 
              : userAttributes?.email || user?.username || 'Admin'}</span>
            <button onClick={signOut} className="sign-out-btn">Sign Out</button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {renderContent()}
      </main>

      {/* Help Dialog */}
      {showHelp && (
        <div className="help-dialog-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>CSV Upload Format</h3>
            <p>Files must be in CSV format with the following fields in order:</p>
            <ol>
              <li>Address1</li>
              <li>Address2</li>
              <li>City</li>
              <li>State</li>
              <li>Zip</li>
              <li>First Name (If available)</li>
              <li>Last Name (If available)</li>
              <li>Email (If available)</li>
              <li>Phone (If available)</li>
            </ol>
            <p>Example:</p>
            <pre>123 Main St,,Atlanta,GA,30309,John,Doe,john@example.com,555-1234</pre>
            <button onClick={() => setShowHelp(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {statusModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            minWidth: '400px'
          }}>
            <h3>Change Status</h3>
            <p>Changing from <strong>{statusModal.currentStatus}</strong> to <strong>{statusModal.newStatus}</strong></p>
            <div style={{ marginBottom: '15px' }}>
              <label>Note (optional):</label>
              <textarea 
                id="statusNote"
                placeholder="Add a note about this status change..."
                style={{
                  width: '100%',
                  height: '80px',
                  marginTop: '5px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button 
                onClick={() => setStatusModal({ show: false, qrCode: '', newStatus: '', currentStatus: '' })}
                style={{ marginRight: '10px', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmStatusChange}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-subscribe Warning Modal */}
      {resubscribeModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            lineHeight: '1.5'
          }}>
            <h3 style={{ color: '#e67e22', marginBottom: '20px' }}>⚠️ Re-subscribe Lead</h3>
            <p style={{ marginBottom: '15px' }}>
              <strong>Lead:</strong> {resubscribeModal.leadName}
            </p>
            <div style={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              padding: '15px', 
              borderRadius: '5px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Important Notice:</p>
              <p style={{ margin: '0 0 10px 0' }}>
                Re-subscribing is normal for leads who have specifically requested to receive marketing communications again. 
                However, re-subscribing a prospect who hasn't explicitly asked for re-subscription can negatively affect 
                your internet reputation and could result in a poor sender reputation score, affecting your ability to 
                send marketing emails.
              </p>
              <p style={{ margin: '0', fontWeight: 'bold' }}>
                By clicking "Re-subscribe", you confirm that you have proper authorization from this prospect 
                to re-subscribe them to your marketing communications.
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <button 
                onClick={() => setResubscribeModal({ show: false, qrCode: '', leadName: '' })}
                style={{ 
                  marginRight: '10px', 
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmResubscribe}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#e67e22',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
              >
                Re-subscribe Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {sendMessageLead && (
        <SendMessage 
          lead={sendMessageLead} 
          onClose={() => setSendMessageLead(null)} 
        />
      )}
    </div>
  );
};

export default AdminDashboard;
